import { useEffect, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  LEFT,
  MEDIUM_VIEWPORT_MEDIA_QUERY,
  RIGHT,
  SCROLL_DISTANCE
} from "../constants";
import { useChildAccessibility, useMediaQuery, useScrollState } from "../hooks";
import { ScreenReaderInstructions } from "./ScreenReaderInstructions";
import CarouselButton from "./CarouselButton";
import { ListElement } from "./ListElement";
import { withTranslation } from "../../translation";
import styles from "./HorizontalScrollListVariant.css";
import { useScrollItemIntoCenter } from "../../hooks/useScrollItemIntoCenter";

const HorizontalScrollListVariant = ({
  scrollSnapping,
  children,
  className,
  ariaLabel,
  formatTranslation
}) => {
  const listRef = useRef(null);
  const isNotMobileViewport = useMediaQuery(MEDIUM_VIEWPORT_MEDIA_QUERY);
  const { hasOverflow, isAtStart, isAtEnd } = useScrollState(listRef, children);
  const scrollItemIntoCenter = useScrollItemIntoCenter(listRef);
  const { handleChildAccessibility } = useChildAccessibility(
    listRef,
    isNotMobileViewport
  );

  useEffect(() => {
    handleChildAccessibility();
  }, [handleChildAccessibility, hasOverflow]);

  useLayoutEffect(() => {
    const selected = listRef.current?.querySelector("span");
    const carouselUl = listRef.current;
    if (carouselUl) {
      if (selected) {
        scrollItemIntoCenter(selected);
      }
    }
  }, [scrollItemIntoCenter, listRef.current]);

  const scroll = direction => {
    if (!listRef.current) return;

    listRef.current.scrollBy({
      left: direction === LEFT ? -SCROLL_DISTANCE : SCROLL_DISTANCE,
      behavior: "smooth"
    });
  };

  const showCarousel = hasOverflow && isNotMobileViewport;

  return (
    <div
      className={classnames(styles.listContainer, className)}
      role={showCarousel ? "region" : undefined}
      aria-label={showCarousel ? formatTranslation(ariaLabel) : undefined}
    >
      {showCarousel && (
        <>
          <ScreenReaderInstructions formatTranslation={formatTranslation} />
          <CarouselButton
            direction={LEFT}
            scroll={scroll}
            disabled={!hasOverflow || isAtStart}
          />
        </>
      )}
      <ListElement
        listRef={listRef}
        scrollSnapping={scrollSnapping}
        showCarousel={showCarousel}
      >
        {children}
      </ListElement>
      {showCarousel && (
        <CarouselButton
          direction={RIGHT}
          scroll={scroll}
          disabled={!hasOverflow || isAtEnd}
        />
      )}
    </div>
  );
};

HorizontalScrollListVariant.propTypes = {
  scrollSnapping: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  formatTranslation: PropTypes.func.isRequired
};

export default withTranslation(HorizontalScrollListVariant);
